import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { OfflineExportsCrmExportTypesEnum } from '../../enums';
import { OfflineExportsApiService } from '../../services/api';
import { CommonUtilService } from '../../services/CommonUtil.service';
import { SearchService } from '../../../results/search/services/search.service';
import { OfflineExportsCrmExportRequestDto, OfflineExportsExportRequestDto } from '../../dto';
import { COMPANY_ASYNC_EXPORT_SERVICE_CONFIG } from './constants';
import { CompanyAsyncExportServiceModel } from './models';
import {  MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LoadingService } from '../../services';

const LOADING_TAG: string = 'company-async-export-loading-tag';
enum PageType {
    DUPLICATES = 'duplicates',
    JOB_TITLE = 'jobTitle',
    FINISHED = 'finished',
}

@UntilDestroy()
@Component({
    selector: 'app-company-async-export',
    templateUrl: './company-async-export.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CompanyAsyncExportComponent implements OnInit {
    readonly isLoading$: Observable<boolean> = this.loadingService.getIsLoading(LOADING_TAG);

    crmType: OfflineExportsCrmExportTypesEnum;
    currentPageIndex: number;
    form: FormGroup;
    showPreviousButton: boolean = true;

    readonly pages: PageType[] = [PageType.DUPLICATES, PageType.JOB_TITLE, PageType.FINISHED];
    readonly pageType: typeof PageType = PageType;

    constructor(
        @Inject(COMPANY_ASYNC_EXPORT_SERVICE_CONFIG) private readonly companyAsyncExportService: CompanyAsyncExportServiceModel,
        private readonly fb: FormBuilder,
        private readonly offlineExportsApiService: OfflineExportsApiService,
        private readonly commonUtilService: CommonUtilService,
        private readonly searchService: SearchService,
        @Inject(MAT_DIALOG_DATA) private readonly data: OfflineExportsExportRequestDto,
        private readonly loadingService: LoadingService,
    ) {}

    ngOnInit(): void {
        this.crmType = this.companyAsyncExportService.crmType;
        this.buildForm();
        this.setDefaultPages();
        this.getSettingsDuplicateResolution()
            .pipe(untilDestroyed(this))
            .subscribe((duplicateResolution: string) => {
                if (duplicateResolution !== 'prompt') {
                    this.form.get('duplicateResolution').setValue(duplicateResolution);
                    this.showPreviousButton = false;
                    this.next();
                }
            });
    }

    export(): void {
        const { duplicateResolution, label } = this.form.getRawValue();
        const crmType: OfflineExportsCrmExportTypesEnum = this.companyAsyncExportService.crmType;
        // this.authenticationService.getToken().pipe(
        //   take(1),
        // ).subscribe((token: TokenInterface) => {
        //     this.socketService.connect();
        //     this.socketService.sendMessage({
        //         action: "salesforceCompaniesExport",
        //         auth: `${token.key} ${token.value}`,
        //         payload: {
        //             export_options: {
        //                 export_settings: {
        //                     duplicate_resolution: duplicateResolution,
        //                 }
        //             },
        //             job: {
        //                 ...this.searchService.criteriaToRequest(this.data),
        //                 label: label || this.commonUtilService.getFileNameByRequestType(crmType)
        //             },
        //         }
        //     });
        //
        //     this.socketService.onMessage().subscribe(console.log);
        // })
        //
        // return;
        this.exportCompanies(crmType, {
            export_options: {
                export_settings: {
                    duplicate_resolution: duplicateResolution,
                }
            },
            job: {
                ...this.searchService.criteriaToRequest(this.data),
                label: label || this.commonUtilService.getFileNameByRequestType(crmType)
            },
        }).pipe(untilDestroyed(this)).subscribe(() => {
            this.next();
        })
    }

    next(): void {
        this.currentPageIndex++;
    }

    previous(): void {
        this.currentPageIndex--;
    }

    private buildForm(): void {
        this.form = this.fb.group({
            duplicateResolution: ['update'],
            label: null,
        });
    }

    private setDefaultPages(): void {
        this.currentPageIndex = 0;
    }

    private exportCompanies(crmType: OfflineExportsCrmExportTypesEnum, body: OfflineExportsCrmExportRequestDto) {
        return this.loadingService.loadingWrapper(this.offlineExportsApiService.exportCrm(crmType, body), LOADING_TAG);
    }

    private getSettingsDuplicateResolution(): Observable<string> {
        return this.loadingService.loadingWrapper(this.companyAsyncExportService.getSettingsDuplicateResolution(), LOADING_TAG);
    }
}
